import type { RouteRecordRaw } from 'vue-router'
import {
  cardAuditRecord,
  cardCashRecord,
  cardListExport,
  cardScoreRecord,
  createBindMobileCoupon,
  createHoliday,
  createHomePopupCoupon,
  createOfflineCoupon,
  createOpenCardCoupon,
  createOpenCardRule,
  createQrCoupon,
  createRechargeCoupon,
  createRechargeRule,
  delivery,
  detailOfflineCoupon,
  editBindMobileCoupon,
  editHoliday,
  editHomePopupCoupon,
  editOfflineCoupon,
  editOpenCardCoupon,
  editOpenCardRule,
  editQrCoupon,
  editRechargeCoupon,
  editRechargeRule,
  payDealExport,
  productOrder,
  productOrderExport,
  rechargeDealExport,
  ticketExport,
  ticketOrder,
  userExport,
} from '@/constants/routerName'
import MzRouterView from '@meizhen/shared/src/components/MzRouterView.vue'

export const initialRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: async () => import('@/views/login/Index.vue'),
  },
  {
    path: '/denied',
    name: '403',
    component: async () => import('@/views/403.vue'),
  },
  {
    path: '/',
    name: 'Root',
    component: async () => import('@/views/layout/Index.vue'),
    meta: {
      menu: true,
      showInBreadcrumb: false,
    },
    beforeEnter: (to) => {
      if (to.name !== 'Root') return

      const { firstPermissedRoute } = useProfileStore()
      const r = firstPermissedRoute(useRouter()) || { name: 'ScanCode' }

      return r
    },
    children: [
      {
        path: 'scan',
        name: 'ScanCode',
        component: async () => import('@/views/scan/ScanCode.vue'),
        meta: {
          title: '扫码核销',
          showInSide: true,
          showInBreadcrumb: true,
          icon: 'scan',
          permissions: 'verify',
        },
      },
      {
        path: 'ticket-issue',
        name: 'InterceptionTicketIssue',
        component: async () => import('@/views/ticket-issue/Index.vue'),
        meta: {
          title: '影票出票',
          showInSide: true,
          showInBreadcrumb: true,
          icon: 'ticket_order',
          permissions: 'interceptionTicket:issue',
          requireInterceptionEnabled: true,
        },
      },
      {
        path: 'card',
        name: 'Card',
        component: MzRouterView,
        meta: {
          title: '会员卡',
          showInSide: true,
          showInBreadcrumb: false,
          icon: 'card_title',
        },
        children: [
          {
            path: 'manger',
            name: 'CardManagerIndex',
            component: async () => import('@/views/card/manager/Index.vue'),
            meta: {
              title: '会员卡管理',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'card',
              permissions: 'card:manage',
            },
          },
          {
            path: 'open',
            name: 'OpenCard',
            component: async () => import('@/views/card/open/Index.vue'),
            meta: {
              title: '开卡',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'open_card',
              permissions: 'card:open',
            },
          },
          {
            path: 'list',
            name: 'CardList',
            redirect: {
              name: 'CardListIndex',
            },
            component: MzRouterView,
            meta: {
              title: '会员卡列表',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'card_list',
              permissions: 'card:list',
            },
            children: [
              {
                path: '',
                name: 'CardListIndex',
                component: async () => import('@/views/card/list/Index.vue'),
              },
              {
                path: 'cash/:id',
                name: cardCashRecord,
                component: async () => import('@/views/card/list/CashRecord.vue'),
                meta: {
                  title: '余额记录',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'card:list',
                },
              },
              {
                path: 'operate/:id',
                name: cardAuditRecord,
                component: async () => import('@/views/card/list/AuditRecord.vue'),
                meta: {
                  title: '操作记录',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'card:list',
                },
              },
              {
                path: 'score/:id',
                name: cardScoreRecord,
                component: async () => import('@/views/card/list/ScoreRecord.vue'),
                meta: {
                  title: '积分记录',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'card:list',
                },
              },
              {
                path: 'export/:tp',
                name: cardListExport,
                component: async () => import('@/views/export/Index.vue'),
                meta: {
                  title: '导出任务',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'card:export',
                },
              },
            ],
          },
          {
            path: 'recharge/deal',
            name: 'RechargeDeal',
            redirect: {
              name: 'RechargeDealIndex',
            },
            component: MzRouterView,
            meta: {
              title: '充值开卡记录',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'recharge_deal',
              permissions: 'cardCash:list',
            },
            children: [
              {
                path: '',
                name: 'RechargeDealIndex',
                component: async () => import('@/views/card/rechargeOpenDeal/Index.vue'),
              },
              {
                path: 'export/:tp',
                name: rechargeDealExport,
                component: async () => import('@/views/export/Index.vue'),
                meta: {
                  title: '导出任务',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'cardCash:export',
                },
              },
            ],
          },
          {
            path: 'pay/deal',
            name: 'PayDeal',
            redirect: {
              name: 'PayDealIndex',
            },
            component: MzRouterView,
            meta: {
              title: '消费扣款记录',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'pay_deal',
              permissions: 'cardCash:list',
            },
            children: [
              {
                path: '',
                name: 'PayDealIndex',
                component: async () => import('@/views/card/payDeal/Index.vue'),
              },
              {
                path: 'export/:tp',
                name: payDealExport,
                component: async () => import('@/views/export/Index.vue'),
                meta: {
                  title: '导出任务',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'cardCash:export',
                },
              },
            ],
          },
          {
            path: 'level',
            name: 'CardLevel',
            redirect: { name: 'CardLevelIndex' },
            component: MzRouterView,
            meta: {
              title: '会员卡等级',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'card_level',
              permissions: 'cardLevel:list',
            },
            children: [
              {
                path: '',
                name: 'CardLevelIndex',
                component: async () => import('@/views/card/level/Index.vue'),
              },
              {
                path: 'edit/:id',
                name: 'EditCardLevel',
                component: async () => import('@/views/card/level/zhongxin/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'cardLevel:update',
                },
              },
            ],
          },
          {
            path: 'score',
            name: 'CardScore',
            component: async () => import('@/views/card/score/Index.vue'),
            meta: {
              title: '积分交易',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'card_score',
              permissions: 'cardScore:list',
            },
          },
        ],
      },
      {
        path: 'order',
        name: 'Order',
        component: MzRouterView,
        meta: {
          title: '订单',
          showInSide: true,
          showInBreadcrumb: false,
          icon: 'order_title',
        },
        children: [
          {
            path: 'ticket',
            name: ticketOrder,
            redirect: {
              name: 'TicketOrderIndex',
            },
            component: MzRouterView,
            meta: {
              title: '影票订单',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'ticket_order',
              onlySupper: true,
              permissions: 'ticketTrade:list',
            },
            children: [
              {
                path: '',
                name: 'TicketOrderIndex',
                component: async () => import('@/views/order/ticket/Index.vue'),
              },
              {
                path: 'export/:tp',
                name: ticketExport,
                component: async () => import('@/views/export/Index.vue'),
                meta: {
                  title: '导出任务',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'ticketTrade:export',
                },
              },
            ],
          },
          {
            path: 'product',
            name: productOrder,
            redirect: {
              name: 'ProductOrderIndex',
            },
            component: MzRouterView,
            meta: {
              title: '卖品订单',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'product_order',
              permissions: 'productTrade:list',
            },
            children: [
              {
                path: '',
                name: 'ProductOrderIndex',
                component: async () => import('@/views/order/product/Index.vue'),
              },
              {
                path: 'export/:tp',
                name: productOrderExport,
                component: async () => import('@/views/export/Index.vue'),
                meta: {
                  title: '导出任务',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'productTrade:export',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'delivery',
        name: delivery,
        component: async () => import('@/views/delivery/Index.vue'),
        meta: {
          title: '交接单',
          showInSide: true,
          showInBreadcrumb: true,
          icon: 'delivery',
          permissions: 'handover',
        },
      },
      {
        path: 'promo',
        name: 'Promo',
        component: MzRouterView,
        meta: {
          title: '促销',
          showInSide: true,
          showInBreadcrumb: false,
          icon: 'discount_title',
        },
        children: [
          {
            path: 'score/double',
            name: 'ScoreDouble',
            redirect: { name: 'ScoreDoubleIndex' },
            meta: {
              title: '积分翻倍',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'score_double',
              onlySupper: true,
              permissions: 'scoreDouble:list',
            },
            children: [
              {
                path: 'index',
                name: 'ScoreDoubleIndex',
                component: async () => import('@/views/promo/score_double/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'edit/:id',
                name: 'EditScoreDouble',
                component: async () => import('@/views/promo/score_double/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'scoreDouble:update',
                },
              },
              {
                path: 'create',
                name: 'CreateScoreDouble',
                component: async () => import('@/views/promo/score_double/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'scoreDouble:update',
                },
              },
            ],
          },
          {
            path: 'reduce',
            name: 'TicketReduce',
            redirect: { name: 'TicketReduceIndex' },
            meta: {
              title: '影票立减',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'ticket_sales',
              onlySupper: true,
              permissions: 'discount:list',
            },
            children: [
              {
                path: 'index',
                name: 'TicketReduceIndex',
                component: async () => import('@/views/promo/reduce/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'edit/:id',
                name: 'EditTicketReduce',
                component: async () => import('@/views/promo/reduce/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'discount:update',
                },
              },
              {
                path: 'create',
                name: 'CreateTicketReduce',
                component: async () => import('@/views/promo/reduce/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'discount:update',
                },
              },
            ],
          },
          {
            path: 'discount',
            name: 'TicketDiscount',
            redirect: { name: 'TicketDiscountIndex' },
            meta: {
              title: '影票折扣',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'ticket_discount',
              onlySupper: true,
              permissions: 'onsale:list',
            },
            children: [
              {
                path: 'index',
                name: 'TicketDiscountIndex',
                component: async () => import('@/views/promo/discount/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'edit/:id',
                name: 'EditTicketDiscount',
                component: async () => import('@/views/promo/discount/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'onsale:update',
                },
              },
              {
                path: 'create',
                name: 'CreateTicketDiscount',
                component: async () => import('@/views/promo/discount/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'onsale:update',
                },
              },
            ],
          },
          {
            path: 'fixed',
            name: 'TicketFixed',
            redirect: { name: 'TicketFixedIndex' },
            meta: {
              title: '影票固定金额',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'ticket_fixed',
              onlySupper: true,
              permissions: 'actfixed:list',
            },
            children: [
              {
                path: 'index',
                name: 'TicketFixedIndex',
                component: async () => import('@/views/promo/actfixed/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'edit/:id',
                name: 'EditTicketFixed',
                component: async () => import('@/views/promo/actfixed/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'actfixed:update',
                },
              },
              {
                path: 'create',
                name: 'CreateTicketFixed',
                component: async () => import('@/views/promo/actfixed/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'actfixed:update',
                },
              },
            ],
          },
          {
            path: 'deduction',
            name: 'ScoreDeduction',
            component: async () => import('@/views/promo/score_deduction/Index.vue'),
            meta: {
              title: '积分抵扣',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'deduction',
              onlySupper: true,
              permissions: 'scoreDeduction:get',
            },
          },
        ],
      },
      {
        path: 'coupon',
        name: 'Coupon',
        component: MzRouterView,
        meta: {
          title: '优惠券管理',
          showInSide: true,
          showInBreadcrumb: false,
          onlySupper: true,
          icon: 'coupon',
        },
        children: [
          {
            path: 'ticket',
            name: 'TicketCoupon',
            redirect: { name: 'TicketCouponIndex' },
            meta: {
              title: '影票优惠券',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_ticket',
              onlySupper: true,
              permissions: 'ticketCoupon:list',
            },
            children: [
              {
                path: 'index',
                name: 'TicketCouponIndex',
                component: async () => import('@/views/coupon/ticket_coupon/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'create',
                name: 'CreateTicketCoupon',
                component: async () => import('@/views/coupon/ticket_coupon/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'ticketCoupon:update',
                },
              },
            ],
          },
          {
            path: 'product',
            name: 'ProductCoupon',
            redirect: { name: 'ProductCouponIndex' },
            meta: {
              title: '卖品优惠券',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_product',
              onlySupper: true,
              permissions: 'productCoupon:list',
            },
            children: [
              {
                path: 'index',
                name: 'ProductCouponIndex',
                component: async () => import('@/views/coupon/product_coupon/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'create',
                name: 'CreateProductCoupon',
                component: async () => import('@/views/coupon/product_coupon/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'productCoupon:update',
                },
              },
            ],
          },
          {
            path: 'pack',
            name: 'CouponPackage',
            redirect: { name: 'CouponPackageIndex' },
            meta: {
              title: '券包设置',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_package',
              onlySupper: true,
              permissions: 'couponPack:list',
            },
            children: [
              {
                path: 'index',
                name: 'CouponPackageIndex',
                component: async () => import('@/views/coupon/pack/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'create',
                name: 'CreateCouponPackage',
                component: async () => import('@/views/coupon/pack/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponPack:update',
                },
              },
            ],
          },
          {
            path: 'bind/mobile',
            name: 'CouponBindMobile',
            redirect: { name: 'CouponBindMobileIndex' },
            meta: {
              title: '绑定手机发券',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_bind_mobile',
              onlySupper: true,
              permissions: 'couponActBindMobile:list',
            },
            children: [
              {
                path: 'index',
                name: 'CouponBindMobileIndex',
                component: async () => import('@/views/coupon/bindMobile/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'create',
                name: createBindMobileCoupon,
                component: async () => import('@/views/coupon/bindMobile/AddOrEdit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActBindMobile:update',
                },
              },
              {
                path: 'edit/:id',
                name: editBindMobileCoupon,
                component: async () => import('@/views/coupon/bindMobile/AddOrEdit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActBindMobile:update',
                },
              },
            ],
          },
          {
            path: 'targeted',
            name: 'CouponTargeted',
            redirect: { name: 'CouponTargetedIndex' },
            meta: {
              title: '定向发券',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_redirect',
              onlySupper: true,
              permissions: 'couponActTargeted:list',
            },
            children: [
              {
                path: 'index',
                name: 'CouponTargetedIndex',
                component: async () => import('@/views/coupon/targeted/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'create',
                name: 'CreateTargeted',
                component: async () => import('@/views/coupon/targeted/AddOrEdit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActTargeted:update',
                },
              },
              {
                path: 'export/:tp',
                name: 'TargetedExport',
                component: async () => import('@/views/export/Index.vue'),
                meta: {
                  title: '导出任务',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'couponActTargeted:export',
                },
              },
            ],
          },
          {
            path: 'home/popup',
            name: 'HomePopupCoupon',
            redirect: { name: 'HomePopupCouponIndex' },
            meta: {
              title: '首页弹窗领券',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_popup',
              onlySupper: true,
              permissions: 'couponActLaunchPopup:list',
            },
            children: [
              {
                path: 'index',
                name: 'HomePopupCouponIndex',
                component: async () => import('@/views/coupon/launchPopup/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'create',
                name: createHomePopupCoupon,
                component: async () => import('@/views/coupon/launchPopup/AddOrEdit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActLaunchPopup:update',
                },
              },
              {
                path: 'edit/:id',
                name: editHomePopupCoupon,
                component: async () => import('@/views/coupon/launchPopup/AddOrEdit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActLaunchPopup:update',
                },
              },
            ],
          },
          {
            path: 'qr',
            name: 'CouponQr',
            redirect: { name: 'CouponQrIndex' },
            meta: {
              title: '扫码领券',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_scan',
              onlySupper: true,
              permissions: 'couponActQr:list',
            },
            children: [
              {
                path: 'index',
                name: 'CouponQrIndex',
                component: async () => import('@/views/coupon/qr/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'create',
                name: createQrCoupon,
                component: async () => import('@/views/coupon/qr/AddOrEdit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActQr:update',
                },
              },
              {
                path: 'edit/:id',
                name: editQrCoupon,
                component: async () => import('@/views/coupon/qr/AddOrEdit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActQr:update',
                },
              },
            ],
          },
          {
            path: 'open/card',
            name: 'OpenCardCoupon',
            redirect: { name: 'OpenCardCouponIndex' },
            meta: {
              title: '开卡送券',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_open_card',
              onlySupper: true,
              permissions: 'couponActOpenCard:list',
            },
            children: [
              {
                path: 'index',
                name: 'OpenCardCouponIndex',
                component: async () => import('@/views/coupon/open_card/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'create',
                name: createOpenCardCoupon,
                component: async () => import('@/views/coupon/open_card/AddOrEdit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActOpenCard:update',
                },
              },
              {
                path: 'edit/:id',
                name: editOpenCardCoupon,
                component: async () => import('@/views/coupon/open_card/AddOrEdit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActOpenCard:update',
                },
              },
            ],
          },
          {
            path: 'recharge',
            name: 'RechargeCoupon',
            redirect: { name: 'RechargeCouponIndex' },
            meta: {
              title: '充值送券',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_recharge',
              onlySupper: true,
              permissions: 'couponActRecharge:list',
            },
            children: [
              {
                path: 'index',
                name: 'RechargeCouponIndex',
                component: async () => import('@/views/coupon/recharge/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'create',
                name: createRechargeCoupon,
                component: async () => import('@/views/coupon/recharge/AddOrEdit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActRecharge:update',
                },
              },
              {
                path: 'edit/:id',
                name: editRechargeCoupon,
                component: async () => import('@/views/coupon/recharge/AddOrEdit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActRecharge:update',
                },
              },
            ],
          },
          {
            path: 'offline',
            name: 'CouponOffline',
            redirect: { name: 'CouponOfflineIndex' },
            meta: {
              title: '线下发券',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_offline',
              onlySupper: true,
              permissions: 'couponActOffline:list',
            },
            children: [
              {
                path: 'index',
                name: 'CouponOfflineIndex',
                component: async () => import('@/views/coupon/offline/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'create',
                name: createOfflineCoupon,
                component: async () => import('@/views/coupon/offline/AddOrEdit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActOffline:update',
                },
              },
              {
                path: 'edit/:id',
                name: editOfflineCoupon,
                component: async () => import('@/views/coupon/offline/AddOrEdit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActOffline:update',
                },
              },
              {
                path: 'detail/:id',
                name: detailOfflineCoupon,
                component: async () => import('@/views/coupon/offline/Detail.vue'),
                meta: {
                  title: '明细',
                  showInSide: false,
                  showInBreadcrumb: true,
                  onlySupper: true,
                  permissions: 'couponActOffline:list',
                },
              },
              {
                path: 'export/:tp',
                name: 'OfflineExport',
                component: async () => import('@/views/export/Index.vue'),
                meta: {
                  title: '导出任务',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'couponActOffline:export',
                },
              },
            ],
          },
          {
            path: 'record',
            name: 'CouponRecord',
            redirect: { name: 'CouponRecordIndex' },
            meta: {
              title: '优惠券记录',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_record',
              onlySupper: true,
              permissions: 'userCoupon:list',
            },
            children: [
              {
                path: 'index',
                name: 'CouponRecordIndex',
                component: async () => import('@/views/coupon/record/Index.vue'),
                meta: {
                  onlySupper: true,
                },
              },
              {
                path: 'export/:tp',
                name: 'CouponRecordDealExport',
                component: async () => import('@/views/export/Index.vue'),
                meta: {
                  title: '导出任务',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'userCoupon:export',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'product',
        name: 'Product',
        component: MzRouterView,
        meta: {
          title: '卖品管理',
          showInSide: true,
          showInBreadcrumb: false,
          icon: 'product_manage',
        },
        children: [
          {
            path: 'list',
            name: 'ProductList',
            redirect: { name: 'ProductListIndex' },
            component: MzRouterView,
            meta: {
              title: '卖品',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'product',
              permissions: 'product:list',
            },
            children: [
              {
                path: 'index',
                name: 'ProductListIndex',
                component: async () => import('@/views/product_manage/list/Index.vue'),
              },
              {
                path: 'create',
                name: 'CreateProduct',
                component: async () => import('@/views/product_manage/list/Edit.vue'),
                meta: {
                  title: '创建卖品',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'product:update',
                },
              },
              {
                path: 'edit/:id',
                name: 'EditProduct',
                component: async () => import('@/views/product_manage/list/Edit.vue'),
                meta: {
                  title: '编辑卖品',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'product:update',
                },
              },
            ],
          },
          {
            path: 'score',
            name: 'ProductScore',
            redirect: { name: 'ProductScoreIndex' },
            component: MzRouterView,
            meta: {
              title: '积分卖品',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'exchange',
              permissions: 'product:list',
            },
            children: [
              {
                path: 'index',
                name: 'ProductScoreIndex',
                component: async () => import('@/views/product_manage/score/Index.vue'),
              },
              {
                path: 'create',
                name: 'CreateProductScore',
                component: async () => import('@/views/product_manage/score/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'product:update',
                },
              },
              {
                path: 'edit/:id',
                name: 'EditProductScore',
                component: async () => import('@/views/product_manage/score/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'product:update',
                },
              },
            ],
          },
          {
            path: 'cat',
            name: 'ProductCat',
            redirect: { name: 'ProductCatIndex' },
            component: MzRouterView,
            meta: {
              title: '分类',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'product_class',
              permissions: 'productCat:list',
            },
            children: [
              {
                path: 'index',
                name: 'ProductCatIndex',
                component: async () => import('@/views/product_manage/cat/Index.vue'),
              },
              {
                path: 'create',
                name: 'CreateProductCat',
                component: async () => import('@/views/product_manage/cat/Edit.vue'),
                meta: {
                  title: '创建分类',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'productCat:update',
                },
              },
              {
                path: 'edit/:id',
                name: 'EditProductCat',
                component: async () => import('@/views/product_manage/cat/Edit.vue'),
                meta: {
                  title: '编辑分类',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'productCat:update',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'user',
        name: 'User',
        redirect: { name: 'UserIndex' },
        component: MzRouterView,
        meta: {
          title: '用户',
          showInSide: true,
          showInBreadcrumb: true,
          icon: 'users',
          onlySupper: true,
          permissions: 'user:list',
        },
        children: [
          {
            path: '',
            name: 'UserIndex',
            component: async () => import('@/views/user/Index.vue'),
          },
          {
            path: 'export/:tp',
            name: userExport,
            component: async () => import('@/views/export/Index.vue'),
            meta: {
              title: '导出任务',
              showInSide: false,
              showInBreadcrumb: true,
              permissions: 'user:export',
            },
          },
        ],
      },
      {
        path: 'crm',
        name: 'CRM',
        component: MzRouterView,
        meta: {
          title: 'CRM',
          showInSide: true,
          showInBreadcrumb: false,
          icon: 'crm',
        },
        children: [
          {
            path: 'staff',
            name: 'StaffList',
            redirect: { name: 'StaffListIndex' },
            component: MzRouterView,
            meta: {
              title: '客服',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'customer_service',
              permissions: 'crmSupport:list',
            },
            children: [
              {
                path: 'index',
                name: 'StaffListIndex',
                component: async () => import('@/views/crm_man/staff/Index.vue'),
              },
              {
                path: 'create',
                name: 'StaffCreate',
                component: async () => import('@/views/crm_man/staff/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'crmSupport:update',
                },
              },
              {
                path: 'edit/:id',
                name: 'StaffEdit',
                component: async () => import('@/views/crm_man/staff/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'crmSupport:update',
                },
              },
            ],
          },
          {
            path: 'customer',
            name: 'CustomerIndex',
            redirect: { name: 'Customer' },
            component: MzRouterView,
            meta: {
              title: '客户',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'customer',
              permissions: 'crmCustomer:list',
            },
            children: [
              {
                path: '',
                name: 'Customer',
                component: async () => import('@/views/crm_man/customer/Index.vue'),
              },
              {
                path: 'detail/:id',
                name: 'CustomerDetail',
                component: async () => import('@/views/crm_man/customer/Detail.vue'),
                meta: {
                  title: '客户详情',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'crmCustomer:list',
                },
              },
              {
                path: 'add',
                name: 'CustomerCreate',
                component: async () => import('@/views/crm_man/customer/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'crmSupport:update',
                },
              },
              {
                path: 'edit/:id',
                name: 'CustomerEdit',
                component: async () => import('@/views/crm_man/customer/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'crmCustomer:update',
                },
              },
            ],
          },
          {
            path: 'reply',
            name: 'CrmReplyIndex',
            component: async () => import('@/views/crm_man/reply/Index.vue'),
            meta: {
              title: '回复文字',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'crm_reply',
              permissions: 'crmRecruit:list',
            },
          },
          {
            path: 'recruit',
            name: 'RecruitIndex',
            component: async () => import('@/views/crm_man/recruit/Index.vue'),
            meta: {
              title: '拉新设置',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'recruit',
              permissions: 'crmRecruit:list',
            },
          },
          {
            path: 'state',
            name: 'CustomerState',
            component: MzRouterView,
            redirect: { name: 'CustomerStateIndex' },
            meta: {
              title: '状态管理',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'crm_state',
              permissions: 'crmState:list',
            },
            children: [
              {
                path: 'index',
                name: 'CustomerStateIndex',
                component: async () => import('@/views/crm_man/customer_state/Index.vue'),
              },
              {
                path: 'create',
                name: 'CustomerStateCreate',
                component: async () => import('@/views/crm_man/customer_state/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'crmState:update',
                },
              },
              {
                path: 'edit/:id',
                name: 'CustomerStateEdit',
                component: async () => import('@/views/crm_man/customer_state/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'crmState:update',
                },
              },
            ],
          },

        ],
      },
      {
        path: 'group/ticket',
        name: 'GroupTicket',
        redirect: { name: 'GroupTicketIndex' },
        component: MzRouterView,
        meta: {
          title: '团体票',
          showInSide: true,
          showInBreadcrumb: true,
          icon: 'ticket_team',
          onlySupper: true,
          permissions: 'groupTicket:list',
        },
        children: [
          {
            path: '',
            name: 'GroupTicketIndex',
            component: async () => import('@/views/group_ticket/Index.vue'),
          },
          {
            path: 'create',
            name: 'CreateGroupTicket',
            component: async () => import('@/views/group_ticket/Edit.vue'),
            meta: {
              title: '创建',
              showInSide: false,
              showInBreadcrumb: true,
              permissions: 'groupTicket:update',
            },
          },
          {
            path: 'edit/:id',
            name: 'EditGroupTicket',
            component: async () => import('@/views/group_ticket/Edit.vue'),
            meta: {
              title: '编辑',
              showInSide: false,
              showInBreadcrumb: true,
              permissions: 'groupTicket:update',
            },
          },
          {
            path: 'detail/:id',
            name: 'GroupTicketDetail',
            component: async () => import('@/views/group_ticket/Detail.vue'),
            meta: {
              title: '详情',
              showInSide: false,
              showInBreadcrumb: true,
              permissions: 'groupTicket:list',
            },
          },
        ],
      },
      {
        path: 'setting',
        name: 'Setting',
        component: MzRouterView,
        meta: {
          title: '设置',
          showInSide: true,
          showInBreadcrumb: false,
          onlySupper: true,
          icon: 'setting_title',
        },
        children: [
          {
            path: 'film',
            name: 'Film',
            redirect: { name: 'FilmIndex' },
            component: MzRouterView,
            meta: {
              title: '影片管理',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'film',
              onlySupper: true,
              permissions: 'film:list',
            },
            children: [
              {
                path: '',
                name: 'FilmIndex',
                component: async () => import('@/views/setting/film/Index.vue'),
              },
              {
                path: 'edit/:id',
                name: 'EditFilm',
                component: async () => import('@/views/setting/film/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'film:update',
                },
              },
            ],
          },
          {
            path: 'recharge',
            name: 'Recharge',
            component: async () => import('@/views/setting/recharge/Index.vue'),
            meta: {
              title: '小程序充值',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'recharge_setting',
              onlySupper: true,
              permissions: 'onlineRecharge:update',
            },
          },
          {
            path: 'openCard/rule',
            name: 'OpenCardRule',
            redirect: { name: 'OpenCardRuleIndex' },
            component: MzRouterView,
            meta: {
              title: '开卡规则',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_open_card',
              onlySupper: true,
              permissions: 'zxOpenCardRule:list',
            },
            children: [
              {
                path: '',
                name: 'OpenCardRuleIndex',
                component: async () => import('@/views/setting/open_card_rule/Index.vue'),
              },
              {
                path: 'create',
                name: createOpenCardRule,
                component: async () => import('@/views/setting/open_card_rule/AddOrEdit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'zxOpenCardRule:update',
                },
              },
              {
                path: 'edit/:id',
                name: editOpenCardRule,
                component: async () => import('@/views/setting/open_card_rule/AddOrEdit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'zxOpenCardRule:update',
                },
              },
            ],
          },
          {
            path: 'recharge/rule',
            name: 'RechargeRule',
            redirect: { name: 'RechargeRuleIndex' },
            component: MzRouterView,
            meta: {
              title: '充值规则',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'coupon_recharge',
              onlySupper: true,
              permissions: 'zxRechargeRule:list',
            },
            children: [
              {
                path: '',
                name: 'RechargeRuleIndex',
                component: async () => import('@/views/setting/recharge_rule/Index.vue'),
              },
              {
                path: 'create',
                name: createRechargeRule,
                component: async () => import('@/views/setting/recharge_rule/AddOrEdit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'zxRechargeRule:update',
                },
              },
              {
                path: 'edit/:id',
                name: editRechargeRule,
                component: async () => import('@/views/setting/recharge_rule/AddOrEdit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'zxRechargeRule:update',
                },
              },
            ],
          },
          {
            path: 'refund/rule',
            name: 'TicketRefundRule',
            component: async () => import('@/views/setting/ticketRefund/Index.vue'),
            meta: {
              title: '影票退款规则',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'ticket_refund_rule',
              onlySupper: true,
              permissions: 'refundRule:update',
            },
          },
          {
            path: 'holiday',
            name: 'Holiday',
            redirect: { name: 'HolidayIndex' },
            component: MzRouterView,
            meta: {
              title: '自定义节假日',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'holiday',
              onlySupper: true,
              permissions: 'holiday:list',
            },
            children: [
              {
                path: '',
                name: 'HolidayIndex',
                component: async () => import('@/views/setting/holiday/Index.vue'),
              },
              {
                path: 'create',
                name: createHoliday,
                component: async () => import('@/views/setting/holiday/Edit.vue'),
                meta: {
                  title: '创建',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'holiday:update',
                },
              },
              {
                path: 'edit/:id',
                name: editHoliday,
                component: async () => import('@/views/setting/holiday/Edit.vue'),
                meta: {
                  title: '编辑',
                  showInSide: false,
                  showInBreadcrumb: true,
                  permissions: 'holiday:update',
                },
              },
            ],
          },
          {
            path: 'cinema',
            name: 'Cinema',
            component: async () => import('@/views/setting/cinema_setting/Index.vue'),
            meta: {
              title: '影院设置',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'cinema_setting',
              onlySupper: true,
              permissions: 'cinemaSetting:update',
            },
          },
          {
            path: 'interception',
            name: 'InterceptionManage',
            component: MzRouterView,
            meta: {
              title: '优化管理',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'setting_title',
              onlySupper: true,
              permissions: 'cinemaSetting:update',
              requireInterceptionEnabled: true, // [ZHONGXIN-INTERCEPTION] 需要拦截总开关开启
            },
            children: [
              {
                path: '',
                name: 'Interception',
                component: async () => import('@/views/setting/interception/Index.vue'),
                meta: {
                  title: '设置',
                  showInSide: true,
                  showInBreadcrumb: true,
                  permissions: 'cinemaSetting:update',
                  requireInterceptionEnabled: true, // [ZHONGXIN-INTERCEPTION] 需要拦截总开关开启
                },
              },
              // [ZHONGXIN-INTERCEPTION] 拦截日志管理路由
              {
                path: 'stats',
                name: 'InterceptionStats',
                component: async () => import('@/views/setting/interception/Stats.vue'),
                meta: {
                  title: '日志管理',
                  showInSide: true,
                  showInBreadcrumb: true,
                  permissions: 'cinemaSetting:update',
                  requireInterceptionEnabled: true, // [ZHONGXIN-INTERCEPTION] 需要拦截总开关开启
                },
              },
              // [ZHONGXIN-INTERCEPTION] 拦截订单管理路由
              {
                path: 'orders',
                name: 'InterceptionOrders',
                component: async () => import('@/views/setting/interception/Orders.vue'),
                meta: {
                  title: '订单管理',
                  showInSide: true,
                  showInBreadcrumb: true,
                  permissions: 'cinemaSetting:update',
                  requireInterceptionEnabled: true, // [ZHONGXIN-INTERCEPTION] 需要拦截总开关开启
                },
              },
              // [ZHONGXIN-INTERCEPTION] 占座任务管理路由
              {
                path: 'seat-tasks',
                name: 'SeatTaskAdmin',
                component: async () => import('@/views/setting/interception/SeatTasks.vue'),
                meta: {
                  title: '任务管理',
                  showInSide: true,
                  showInBreadcrumb: true,
                  permissions: 'cinemaSetting:update',
                  requireInterceptionEnabled: true, // [ZHONGXIN-INTERCEPTION] 需要拦截总开关开启
                },
              },
              // [ZHONGXIN-INTERCEPTION] 本地座位锁管理路由
              {
                path: 'seat-holds',
                name: 'SeatHoldAdmin',
                component: async () => import('@/views/setting/interception/SeatHolds.vue'),
                meta: {
                  title: '锁管理',
                  showInSide: true,
                  showInBreadcrumb: true,
                  permissions: 'cinemaSetting:update',
                  requireInterceptionEnabled: true,
                },
              },
            ],
          },
        ],
      },
      {
        path: 'account',
        component: MzRouterView,
        meta: {
          title: '账号管理',
          showInSide: true,
          showInBreadcrumb: false,
          onlySupper: true,
          icon: 'account',
        },
        children: [
          {
            path: '',
            name: 'Account',
            component: async () => import('@/views/admin/account/Index.vue'),
            meta: {
              title: '管理员',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'staff',
              onlySupper: true,
              permissions: 'staff:list',
            },
          },
          {
            path: 'role',
            name: 'Role',
            component: async () => import('@/views/admin/role/Index.vue'),
            meta: {
              title: '角色管理',
              showInSide: true,
              showInBreadcrumb: true,
              icon: 'role',
              onlySupper: true,
              permissions: 'role:list',
            },
          },
        ],
      },
    ],
  },
]
